import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import InstallPrompt from "./components/InstallPrompt";
import ReadingAssistant from "./components/ReadingAssistant";
import { PracticeReminderScheduler } from "./components/PracticeReminderCard";
import SiteFooter from "./components/SiteFooter";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Curriculum from "./pages/Curriculum";
import Pillar from "./pages/Pillar";
import Lesson from "./pages/Lesson";
import Tutor from "./pages/Tutor";
import Games from "./pages/Games";
import Assessment from "./pages/Assessment";
import Profile from "./pages/Profile";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminStudents from "./pages/AdminStudents";
import Pricing from "./pages/Pricing";
import Bundle from "./pages/Bundle";
import RefundPolicy from "./pages/RefundPolicy";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import LetterSoundGame from "./pages/games/LetterSoundGame";
import BlendingGame from "./pages/games/BlendingGame";
import SegmentingGame from "./pages/games/SegmentingGame";
import WordBuildGame from "./pages/games/WordBuildGame";
import RhymeGame from "./pages/games/RhymeGame";
import SightWordsGame from "./pages/games/SightWordsGame";
import UnscrambleGame from "./pages/games/UnscrambleGame";
import ComprehensionGame from "./pages/games/ComprehensionGame";

function ProtectedLayout() {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
                <p className="text-[#515B57]">Loading…</p>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;

    // Placement gate: every learner must complete the placement test before
    // unlocking lessons / games / tutor / dashboard. Admins are exempt.
    // Profile and the assessment page itself are always reachable.
    const needsAssessment = !user.assessment_result && user.role !== "admin";
    const allowedWithoutAssessment = ["/app/assessment", "/app/profile"];
    const isAllowed = allowedWithoutAssessment.some((p) =>
        location.pathname === p || location.pathname.startsWith(p + "/")
    );
    if (needsAssessment && !isAllowed) {
        return <Navigate to="/app/assessment" replace />;
    }

    return (
        <div className="App">
            <Navbar />
            <Outlet />
        </div>
    );
}

function PublicLayout() {
    return (
        <div className="App">
            <Navbar />
            <Outlet />
            <SiteFooter />
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-center" richColors />
                <InstallPrompt />
                <ReadingAssistant />
                <PracticeReminderScheduler />
                <Routes>
                    <Route element={<PublicLayout />}>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/bundle" element={<Bundle />} />
                        <Route path="/refund" element={<RefundPolicy />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/payment/success" element={<PaymentSuccess />} />
                        <Route path="/payment/cancel" element={<PaymentCancel />} />
                    </Route>
                    <Route path="/app" element={<ProtectedLayout />}>
                        <Route index element={<Dashboard />} />
                        <Route path="curriculum" element={<Curriculum />} />
                        <Route path="curriculum/:pillarId" element={<Pillar />} />
                        <Route path="curriculum/:pillarId/lesson/:lessonId" element={<Lesson />} />
                        <Route path="tutor" element={<Tutor />} />
                        <Route path="games" element={<Games />} />
                        <Route path="assessment" element={<Assessment />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="admin/analytics" element={<AdminAnalytics />} />
                        <Route path="admin/students" element={<AdminStudents />} />
                        <Route path="games/letter-sound" element={<LetterSoundGame />} />
                        <Route path="games/blending" element={<BlendingGame />} />
                        <Route path="games/segmenting" element={<SegmentingGame />} />
                        <Route path="games/word-build" element={<WordBuildGame />} />
                        <Route path="games/rhyme" element={<RhymeGame />} />
                        <Route path="games/sight-words" element={<SightWordsGame />} />
                        <Route path="games/unscramble" element={<UnscrambleGame />} />
                        <Route path="games/comprehension" element={<ComprehensionGame />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
